import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ApiService } from '../services/apiService';
import { useReturningCustomerFlow } from '../hooks/useReturningCustomerFlow';
import { StatusPageLayout } from '../components/layout/StatusPageLayout';
import { AuthService } from '../services/authService';
// @mcc-dev-preview:import
import { parseDevState } from '../dev/devPreview';
import './StartVerification.css';

// @mcc-dev-preview:start dev-preview-types
type StartVerificationDevState = 'loading' | 'success' | 'error';

const START_VERIFICATION_DEV_STATES: StartVerificationDevState[] = [
  'loading',
  'success',
  'error',
];
// @mcc-dev-preview:end dev-preview-types

interface VerificationState {
  status: 'loading' | 'success' | 'error' | 'idle';
  message: string;
  user?: unknown;
}

const StartVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const devState = parseDevState(searchParams, START_VERIFICATION_DEV_STATES);
  const [verificationState, setVerificationState] = useState<VerificationState>({
    status: 'idle',
    message: ''
  });

  const token = searchParams.get('token');
  const cardId = searchParams.get('cardId');
  const returnTo = searchParams.get('returnTo');

  const {
    showCardLinkingDialog,
    returningCustomerName,
    handleReturningCustomer,
    handleDialogOk,
    handleDialogCancel
  } = useReturningCustomerFlow();

  const verifyToken = useCallback(async (verificationToken: string) => {
    try {
      setVerificationState({
        status: 'loading',
        message: 'Verifying your email...'
      });

      const result = await ApiService.verifyEmailToken(verificationToken);

      if (result.success) {
        setVerificationState({
          status: 'success',
          message: 'Email verified successfully!',
          user: result.data
        });

        if (result.data.jwtToken) {
          localStorage.setItem('authToken', result.data.jwtToken);
          localStorage.setItem('user', JSON.stringify(result.data));
        }
        if (result.data?.jwtToken) {
          AuthService.setToken(result.data.jwtToken, {
            userEmail: result.data.userEmail,
            ...result.data
          });
        }

        handleReturningCustomer(
          result.data.isReturningCustomer || false,
          result.data.firstName || '',
          cardId || '',
          returnTo
        );

        if (!result.data.isReturningCustomer) {
          try {
            if (cardId) {
              const linkResult = await ApiService.addCardToUserByEmail(cardId);
              if (!linkResult.success) {
                console.warn('Card link after email verification failed:', linkResult.message);
              }
            }
          } catch (e) {
            console.warn('Card link after email verification threw an error:', e);
          }

          if (returnTo) {
            navigate(returnTo);
          } else if (cardId) {
            navigate(`/kyc-verification?cardId=${encodeURIComponent(cardId)}`);
          } else {
            setVerificationState({
              status: 'error',
              message:
                'A card reference is required to continue to identity verification. Please open the link from your card or contact support.'
            });
          }
        }
      } else {
        setVerificationState({
          status: 'error',
          message: result.message || 'Verification failed'
        });
      }
    } catch (error) {
      console.error('Error verifying token:', error);
      setVerificationState({
        status: 'error',
        message: 'Network error. Please try again.'
      });
    }
  }, [navigate, cardId, returnTo, handleReturningCustomer]);

  // @mcc-dev-preview:start dev-preview-seed
  useEffect(() => {
    if (!devState) return;

    switch (devState) {
      case 'loading':
        setVerificationState({ status: 'loading', message: 'Verifying your email...' });
        break;
      case 'success':
        setVerificationState({
          status: 'success',
          message: 'Email verified successfully!',
        });
        break;
      case 'error':
        setVerificationState({
          status: 'error',
          message: searchParams.get('errorMessage') ?? 'Verification failed',
        });
        break;
      default:
        break;
    }
  }, [devState, searchParams]);
  // @mcc-dev-preview:end dev-preview-seed

  useEffect(() => {
    if (devState) return;
    if (token) {
      verifyToken(token);
    } else {
      setVerificationState({
        status: 'error',
        message: 'No verification token found in URL'
      });
    }
  }, [token, verifyToken, devState]);

  const statusVariant =
    verificationState.status === 'success'
      ? 'success'
      : verificationState.status === 'error'
        ? 'error'
        : 'loading';

  const title =
    verificationState.status === 'success'
      ? 'Email verified'
      : verificationState.status === 'error'
        ? 'Verification failed'
        : 'Verifying your email';

  return (
    <>
      <StatusPageLayout
        variant={statusVariant}
        title={title}
        body={verificationState.message}
        actions={
          verificationState.status === 'error' ? (
            <button type="button" className="ev-btn-primary" onClick={() => navigate('/welcome')}>
              Return to home
            </button>
          ) : undefined
        }
      />
      {showCardLinkingDialog && (
        <div className="card-linking-dialog-overlay">
          <div className="card-linking-dialog">
            <div className="card-linking-dialog-content">
              <h2>Welcome back{returningCustomerName ? ` ${returningCustomerName}` : ''}!</h2>
              <p>If you continue, this card will be linked to your existing account.</p>
              <div className="card-linking-dialog-buttons">
                <button type="button" onClick={handleDialogOk} className="dialog-button dialog-button-ok">
                  OK
                </button>
                <button type="button" onClick={handleDialogCancel} className="dialog-button dialog-button-cancel">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StartVerification;
